// contentEngine.js
//
// The depth layer. Recipe and reference pages already compute correct
// numbers; this works out what is worth SAYING about them on this
// particular page, and nowhere else.
//
// -----------------------------------------------------------------------
// WHAT THIS DELIBERATELY DOES NOT DO
//
// It does not rotate synonyms, shuffle sentence templates, or paraphrase
// the same paragraph into fifty variants. That approach — spinning — makes
// pages differ in wording while saying the same thing, which is precisely
// the pattern search engines are built to catch, and it makes the pages
// worse to read for the humans who are the actual point.
//
// Instead every block below is derived from something that genuinely
// varies between pages: an exposure figure, a hardware constraint, how
// this body compares to its siblings in the same light, a conflict between
// what the shot wants and what the camera can do. When two cameras face
// identical constraints the text SHOULD be similar — that's honest — and
// the thinness audit (scripts/audit-thinness.js) is there to find the
// pages where that means one of them shouldn't exist.
//
// The test applied to every block: would a person who owns this camera
// learn something here they couldn't get from the settings table?
// If not, the block returns nothing rather than padding.
// -----------------------------------------------------------------------

import { generateRecipe } from "./ruleEngine";

// ------------------------------------------------------------- comparisons

/**
 * Where this camera sits against others in the same conditions. This is
 * the single richest source of page-specific content, because it's
 * genuinely unique per camera and impossible to get from a spec sheet —
 * it takes running the same math across the whole database.
 */
function comparativeContext(camera, scenario, lighting, ctx) {
  const { cameras, movements, looks } = ctx;
  const peers = cameras.filter(
    (c) => c.id !== camera.id && c.type === camera.type && c.capabilityLevel === "full" && c.capabilities.aperture?.min
  );
  if (!camera.capabilities.aperture?.min || peers.length < 2) return null;

  const movement = movements.find((m) => m.id === scenario.movementId);
  const look = looks.find((l) => l.id === "natural") || looks[0];

  const mine = generateRecipe({ camera, scenario, lighting, movement, look });
  const myStops = mine.settings.ndRequiredStops;
  if (myStops == null) return null;

  const peerStops = peers
    .map((c) => {
      const r = generateRecipe({ camera: c, scenario, lighting, movement, look });
      return { name: c.name, stops: r.settings.ndRequiredStops };
    })
    .filter((p) => p.stops != null);
  if (peerStops.length < 2) return null;

  const avg = peerStops.reduce((a, p) => a + p.stops, 0) / peerStops.length;
  const diff = Math.round((myStops - avg) * 10) / 10;
  const brightest = peerStops.reduce((a, b) => (b.stops > a.stops ? b : a));

  if (Math.abs(diff) < 0.4) {
    return {
      title: "How this compares to similar cameras",
      text: `Running the same conditions across every ${camera.type.replace(/-/g, " ")} in the database puts the ${camera.name} within half a stop of the group. Whatever filter works for the others here will work for this one, useful if you shoot more than one body and want to carry a single set of filters rather than a set per camera.`,
    };
  }

  const harder = diff > 0;
  return {
    title: "How this compares to similar cameras",
    text: harder
      ? `The ${camera.name} needs about ${Math.abs(diff)} stops more filtration than the average comparable camera in this light, because of its f/${camera.capabilities.aperture.min} lens. That's a real practical difference: a filter set that's adequate on a slower camera can leave you short here, and the gap widens as the light gets brighter. If you're buying one set, buy for this body.`
      : `The ${camera.name} needs roughly ${Math.abs(diff)} stops less filtration here than comparable cameras, its f/${camera.capabilities.aperture.min} lens gathers less light, which for once works in your favour. The ${brightest.name} needs noticeably more in the same conditions. Filters bought for that camera will over-filter on this one.`,
  };
}

// -------------------------------------------------------------- constraints

/**
 * Conflicts between what the shot asks for and what the hardware allows.
 * These are the paragraphs worth reading, because they're where someone
 * would otherwise get caught out mid-shoot.
 */
function constraintNotes(camera, scenario, lighting, recipe) {
  const notes = [];
  const s = recipe.settings;
  const caps = camera.capabilities;

  // Strongest filter still not enough
  if (s.ndRequiredStops != null && s.ndChosenStops != null && s.ndRequiredStops - s.ndChosenStops > 0.5) {
    const short = Math.round((s.ndRequiredStops - s.ndChosenStops) * 10) / 10;
    notes.push({
      title: "You will run out of filter here",
      text: `${lighting.name.toLowerCase()} asks for ${s.ndRequiredStops} stops and the strongest filter made for this camera gives ${s.ndChosenStops}. That leaves you around ${short} stops over with nothing left to add. The practical options are to shoot earlier or later in the day, put the sun behind you, stack a second filter if your set allows it, or accept a faster shutter and the sharper motion that comes with it. Worth deciding before you're standing there, not after.`,
    });
  }

  // No ND at all in bright light
  if (caps.ndSupport === false && lighting.ndLikely) {
    notes.push({
      title: "This camera has no filter thread",
      text: `${caps.ndNote || "There's no first-party ND option for this body."} In ${lighting.name.toLowerCase()} that leaves shutter speed as the only exposure control, and raising it is what makes action-camera footage look harsh and stuttery on pans. A third-party clip-on is genuinely the difference between usable and not here, it's the cheapest meaningful upgrade for this camera.`,
    });
  }

  // Frame rate forced a resolution drop
  if (s.resolutionCapped || (s.resolutionNote && /can't hit|drops to/i.test(s.resolutionNote))) {
    notes.push({
      title: "Frame rate is costing you resolution",
      text: `${s.fps}fps isn't available at this camera's top resolution, so the recipe uses ${s.resolution}. That trade is usually correct, motion that stutters is more noticeable than a resolution step most viewers never see, but if the delivery spec demands the higher resolution, drop the frame rate instead and accept less flexibility in the edit.`,
    });
  }

  // ISO pinned at the ceiling
  if (s.iso && /maximum/i.test(s.iso)) {
    notes.push({
      title: "You're at the top of the ISO range",
      text: `${lighting.name.toLowerCase()} pushes this camera to its published ceiling, which means there is no headroom left. If the image is still dark at these settings, more ISO isn't the answer, add light, open up if the lens allows it, or accept a slower shutter and the motion blur it brings. Pushing a small sensor past its limit produces noise that no amount of post-processing removes cleanly.`,
    });
  }

  // Log profile on a camera whose style controls lock out
  if (s.requiresGrading && caps.imageStyle?.note && /disabled|only available/i.test(caps.imageStyle.note)) {
    notes.push({
      title: "Log locks out the style controls",
      text: `${caps.imageStyle.note} This catches people out: they set a picture style they like, switch to the flat profile for latitude, and find their settings greyed out. It's correct behaviour, those adjustments would be baked in and fight the grade, but it does mean choosing the profile first and the look afterwards.`,
    });
  }

  // Internal-storage-only body
  if (caps.internalStorageOnly) {
    notes.push({
      title: "No card slot to swap",
      text: `This body records to fixed internal storage, so running out mid-shoot isn't something you solve by reaching into a pocket. Offload between sessions rather than assuming you can free space in the field, and check remaining capacity before anything you can't reshoot.`,
    });
  }

  // Mixed lighting has no right answer
  if (lighting.wbKelvin === null) {
    notes.push({
      title: "There is no correct white balance here",
      text: `Two or more colour temperatures in one frame means any single Kelvin value is wrong somewhere. Set for whatever is lighting your subject's face and let the background go warm or cool, an audience forgives an orange window behind a correctly-toned person, and never forgives the reverse.`,
    });
  }

  return notes;
}

// ----------------------------------------------------------------- mistakes

/**
 * Failure modes specific to these numbers. Derived, not a generic list —
 * each only appears when the recipe actually creates the conditions for it.
 */
function commonMistakes(camera, scenario, lighting, recipe) {
  const out = [];
  const s = recipe.settings;

  if (s.fps >= 60) {
    out.push(`Shooting ${s.fps}fps and editing on a ${s.fps}fps timeline. If the point of the high frame rate is slow motion, the timeline needs to be 24 or 30, otherwise you've spent bitrate and card space on nothing and the footage plays back at normal speed.`);
  }
  if (s.ndNeeded && s.ndStatus === "recommended") {
    out.push(`Leaving the filter on when the light drops. ${s.ndFilter} is right for ${lighting.name.toLowerCase()}, but the same filter twenty minutes later, or the moment you step into shade, will have you raising ISO to compensate for glass you could simply remove.`);
  }
  if (s.whiteBalanceMode === "Manual") {
    out.push(`Leaving white balance on Auto because it "looks fine on the screen". It does, shot by shot, the problem only appears in the edit, when two clips from the same afternoon refuse to match.`);
  }
  if (s.requiresGrading) {
    out.push(`Judging exposure by how the flat profile looks on the screen. It is meant to look washed out. Expose using the histogram or zebras, not your eyes, or you'll consistently underexpose and find the noise when you grade.`);
  }
  if (scenario.movementId === "walking" || scenario.movementId === "tracking") {
    out.push(`Relying on stabilization to fix a walking rhythm. Electronic stabilization smooths high-frequency shake; it does nothing for the slow vertical bob of a normal stride. Rolling your feet heel-to-toe and keeping your knees soft does more than any setting on this list.`);
  }
  if (camera.capabilities.focusMode === "fixed") {
    out.push(`Getting closer to fix soft footage. This lens has fixed focus with a deep depth of field, inside roughly 30cm nothing will be sharp, and moving in makes it worse rather than better.`);
  }

  return out.slice(0, 4);
}

// -------------------------------------------------------------- opening line

/**
 * An opening that reflects what's actually distinctive about this
 * combination, so the page doesn't start with the same sentence as the
 * other four hundred.
 */
function opener(camera, scenario, lighting, recipe) {
  const s = recipe.settings;

  if (s.ndStatus === "recommended-unsupported") {
    return `Most of the difficulty in this combination is a filter thread the ${camera.name} doesn't have. The settings below get you as close as the hardware allows, and the section on filtration explains what the gap costs you.`;
  }
  if (s.ndRequiredStops != null && s.ndRequiredStops >= 6) {
    return `${lighting.name} puts roughly ${s.ndRequiredStops} stops more light on this sensor than a ${s.shutter} shutter can absorb, so filtration does most of the work here, the rest of the settings follow from it.`;
  }
  if (s.iso && /maximum/i.test(s.iso)) {
    return `${lighting.name.toLowerCase()} is at the edge of what the ${camera.name} can expose cleanly. These settings sit at the camera's limits rather than in its comfortable middle, which is worth knowing before you commit a shoot to them.`;
  }
  if (s.ndStatus === "not-needed" && s.fps >= 60) {
    return `A straightforward combination: enough light for ${s.fps}fps without filtration, which means the decisions here are about motion rather than exposure.`;
  }
  if (s.requiresGrading) {
    return `These settings assume the footage is going through a grade. If it isn't, if this is going straight to a platform the same day, the profile recommendation is the one thing to change.`;
  }
  return `${scenario.description} On the ${camera.name} in ${lighting.name.toLowerCase()}, that comes down to the settings below and one or two constraints specific to this body.`;
}

// ------------------------------------------------------------ merged pages

/**
 * When a page covers several situations that resolve to the same settings,
 * the most valuable thing it can say is WHY they're the same. That is a
 * real question with a real answer, and it's an answer no cheat-sheet
 * competitor gives — they just publish the table again under a new title.
 */
function mergedExplanation(camera, covers, ctx, recipe) {
  if (!covers || !covers.length) return null;
  const caps = camera.capabilities;

  const names = covers
    .map((c) => {
      const sc = ctx.scenarios.find((x) => x.id === c.scenarioId);
      const li = ctx.lightingConditions.find((x) => x.id === c.lightingId);
      return sc && li ? `${sc.name.toLowerCase()} in ${li.name.toLowerCase()}` : null;
    })
    .filter(Boolean);

  const noManual = !caps.shutterOptions && !caps.isoRange && caps.manualWhiteBalance !== true;
  const lightingVaries = new Set(covers.map((c) => c.lightingId)).size > 1;

  let why;
  if (noManual) {
    why = `The ${camera.name} doesn't expose manual shutter, ISO or white balance in its native camera app, so there is nothing for the scenario to change. The camera makes those decisions itself, and it makes the same ones whether you point it at a plate of food or a person. Everything you actually control, resolution, frame rate, profile, framing, is identical across all of these.`;
  } else if (lightingVaries) {
    why = `These conditions land in the same exposure band on this camera. The light differs by less than the gap between one filter and the next, so the recipe doesn't move, which is worth knowing in itself, because it means you don't need to change anything as the light shifts between them.`;
  } else {
    why = `These situations share a frame rate and an exposure, and on this camera nothing else in the recipe depends on which of them you're shooting. The differences are in framing, movement and how you cut it, not in the settings.`;
  }

  const useful = noManual
    ? `If you want the settings to differ between these, the change isn't a menu on the phone, it's a third-party camera app that exposes manual shutter and ISO. That's the real answer to "what settings should I use for X on this phone", and it's the same answer for all of them.`
    : `Where these do diverge is in the parts a settings table can't hold: how long you hold a shot, how close you get, whether you move. The recipe below is the floor, not the whole job.`;

  return {
    title: `Why these settings are the same for ${names.length + 1} different shots`,
    text: `${why} ${useful}`,
    covered: names,
  };
}

// -------------------------------------------------------------------- entry

/**
 * @param {object} args - { camera, scenario, lighting, recipe }
 * @param {object} ctx - { cameras, movements, looks }
 * @param {object} [editorial] - optional human-written notes for this page
 */
export function generateDepthContent({ camera, scenario, lighting, recipe, covers }, ctx, editorial) {
  const sections = [];

  // A merged page leads with why it's merged — that's its distinguishing
  // content, and burying it below the comparison would waste it.
  const merged = covers && covers.length ? mergedExplanation(camera, covers, ctx, recipe) : null;
  if (merged) sections.push({ title: merged.title, text: merged.text });

  const comparison = comparativeContext(camera, scenario, lighting, ctx);
  if (comparison) sections.push(comparison);

  constraintNotes(camera, scenario, lighting, recipe).forEach((n) => sections.push(n));

  // Human-written notes always sort to the top. Anything a person actually
  // observed on set outranks anything derived here, and it's the only part
  // of the page that can carry first-hand experience.
  const human = editorial && editorial.notes ? editorial.notes : [];

  return {
    opener: merged
      ? `${opener(camera, scenario, lighting, recipe)} This page also covers ${merged.covered.slice(0, 3).join(", ")}${merged.covered.length > 3 ? `, and ${merged.covered.length - 3} more` : ""}, on this camera they all resolve to the same recipe, and the section below explains why.`
      : opener(camera, scenario, lighting, recipe),
    merged,
    humanNotes: human,
    sections,
    mistakes: commonMistakes(camera, scenario, lighting, recipe),
    hasDepth: sections.length > 0 || human.length > 0,
  };
}
